import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, CircularProgress, Divider, Paper, Grid } from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';
import { useAuth } from './context/AuthContext';
// ✅ Import new icons
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const API_BASE_URL = 'https://api.athlete.academy.marsreel.com/api';

// --- STYLED COMPONENTS ---
const Root = styled('div')({ backgroundColor: '#1c1c1c', minHeight: '100vh', color: '#fff', paddingTop: '64px', paddingBottom: '4rem' });
const HeaderImage = styled(Box)({ height: '40vh', width: '100%', backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#333' });
const ContentPaper = styled(Paper)({ padding: '2rem', marginTop: '-5rem', backgroundColor: '#202020', color: '#fff', borderRadius: '12px', boxShadow: '0px 10px 30px rgba(0,0,0,0.5)' });
const Footer = styled(Box)({ width: '100%', padding: '2rem 0', textAlign: 'center', fontSize: '0.9rem', color: '#b0b0b0', borderTop: '1px solid #303030', backgroundColor: '#141414', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '4rem' });

// ✅ NEW: Helper function for date formatting
function formatEventDate(startStr, endStr) {
    if (!startStr || !endStr) return 'Date not available';
    const startDate = new Date(startStr);
    const endDate = new Date(endStr);
    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const datePart = startDate.toLocaleDateString(undefined, dateOptions);
    const startTimePart = startDate.toLocaleTimeString(undefined, timeOptions);
    const endTimePart = endDate.toLocaleTimeString(undefined, timeOptions);
    return `${datePart} • ${startTimePart} - ${endTimePart}`;
}

// ✅ NEW: Detail item component styled like event.html
const DetailItem = ({ icon, label, value }) => (
    <Box sx={{ backgroundColor: '#2a2a2a', borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%' }}>
        {icon}
        <Typography variant="body2" sx={{ color: 'grey.500' }}>{label}</Typography>
        <Typography variant="body1" fontWeight="600" component="div">{value}</Typography>
    </Box>
);

const SessionDetailPage = () => {
    const { sessionId } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [sessionDetails, setSessionDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActionLoading, setIsActionLoading] = useState(false);

    const fetchSessionDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/sessions/${sessionId}`);
            setSessionDetails(response.data);
        } catch (err) {
            console.error("Failed to fetch session details:", err);
            setError("Could not find the requested session.");
        } finally {
            setIsLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        fetchSessionDetails();
    }, [fetchSessionDetails]);

    const handleReserve = async () => {
        if (!currentUser) { navigate('/login'); return; }
        setIsActionLoading(true);
        try {
            await axios.post(`${API_BASE_URL}/sessions/reserve`, { firebaseUid: currentUser.uid, sessionId });
            alert('Reservation successful!');
            fetchSessionDetails();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to reserve session.");
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!currentUser) return;
        const isConfirmed = window.confirm("Are you sure you want to cancel this reservation?");
        if (isConfirmed) {
            setIsActionLoading(true);
            try {
                await axios.post(`${API_BASE_URL}/sessions/cancel`, { firebaseUid: currentUser.uid, sessionId });
                alert('Cancellation successful!');
                fetchSessionDetails();
            } catch (err) {
                alert(err.response?.data?.error || "Failed to cancel reservation.");
            } finally {
                setIsActionLoading(false);
            }
        }
    };

    const renderPageContent = () => {
        if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: '#12c099' }} /></Box>;
        if (error) return <Box sx={{ textAlign: 'center', pt: 8 }}><Typography color="error">{error}</Typography></Box>;
        if (!sessionDetails) return null;

        const hasReserved = currentUser && sessionDetails?.reservedBy?.includes(currentUser.uid);
        const seatsRemaining = sessionDetails.capacity - sessionDetails.reservations;
        const formattedDate = formatEventDate(sessionDetails.eventStartTime, sessionDetails.eventEndTime);

        return (
            <>
                <HeaderImage sx={{ backgroundImage: `url(${sessionDetails.venueImage1})` }} />
                <Container maxWidth="md" sx={{ pb: 4 }}>
                    <ContentPaper>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={7}>
                                <Typography variant="h3" fontWeight="bold">{sessionDetails.name}</Typography>
                                <Typography variant="h6" sx={{ color: 'grey.400', mb: 2 }}>Hosted by {sessionDetails.hostName}</Typography>
                                <Divider sx={{ my: 2, borderColor: 'grey.800' }} />
                                <Typography fontWeight="bold" gutterBottom>About this clinic</Typography>
                                <Typography variant="body1" sx={{ color: 'grey.300' }}>{sessionDetails.description}</Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={12}>
                                        <DetailItem
                                            icon={<CalendarTodayIcon sx={{ color: 'grey.500' }} />}
                                            label="Date & Time"
                                            value={formattedDate}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={12}>
                                        <DetailItem
                                            icon={<CreditCardIcon sx={{ color: 'grey.500' }} />}
                                            label="Cost"
                                            value={`${sessionDetails.creditsRequired} Credits`}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={12}>
                                        <DetailItem
                                            icon={<LocationOnIcon sx={{ color: 'grey.500' }} />}
                                            label="Location"
                                            value={currentUser ? sessionDetails.address : (
                                                <Button
                                                    variant="text"
                                                    onClick={() => navigate('/login')}
                                                    sx={{ p: 0, justifyContent: 'flex-start', color: '#12c099', textTransform: 'none', fontSize: '1rem', lineHeight: 1.5 }}
                                                >
                                                    Log in to view
                                                </Button>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={12}>
                                        <DetailItem
                                            icon={<EventSeatIcon sx={{ color: 'grey.500' }} />}
                                            label="Spots Left"
                                            value={seatsRemaining > 0 ? seatsRemaining : 'Fully Booked'}
                                        />
                                    </Grid>
                                </Grid>

                                <Box mt={3}>
                                    {hasReserved ? (
                                        <Button fullWidth variant="outlined" size="large" onClick={handleCancel} disabled={isActionLoading} sx={{ color: '#ff4d4d', borderColor: '#ff4d4d', '&:hover': { borderColor: '#ff1a1a' } }}>
                                            {isActionLoading ? <CircularProgress size={24} color="inherit"/> : 'Cancel Reservation'}
                                        </Button>
                                    ) : (
                                        <Button fullWidth variant="contained" size="large" onClick={handleReserve} disabled={isActionLoading || seatsRemaining <= 0} sx={{ backgroundColor: '#12c099', color: '#fff', '&:hover': { backgroundColor: '#0f9f85' }, '&:disabled': { backgroundColor: 'grey.700' } }}>
                                            {isActionLoading ? <CircularProgress size={24} color="inherit"/> : (seatsRemaining <= 0) ? 'Fully Booked' : 'Reserve Session'}
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </ContentPaper>
                </Container>
            </>
        );
    }

    return (
        <Root>
            {renderPageContent()}
            <Footer>
                <Typography variant="body2">© 2025 United Artificial Intelligence Company, Inc. All rights reserved.</Typography>
            </Footer>
        </Root>
    );
};

export default SessionDetailPage;